import {EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal} from '@angular/core';
import {Router} from "@angular/router";
import {CustomerService} from "./customer.service";
import {ModuleConfigurationService} from './module-configuration.service';
import {environment} from '../../../environments/environment';
import {ApiService} from './api.service';

@Injectable({
  providedIn: 'root'
})
export class RouterService {
  private router: Router = inject(Router);
  private customerService: CustomerService = inject(CustomerService);
  private moduleConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private apiService: ApiService = inject(ApiService);
  private readonly postLoginRedirectStorageKey: string = 'postLoginRedirectUrl';

  private currentTargetSignal: WritableSignal<string> = signal<string>('');
  public readonly currentTarget: Signal<string> = this.currentTargetSignal.asReadonly();
  private triggerStartNavigation: WritableSignal<boolean> = signal<boolean>(false);

  public navigateTo(target: string): void {
    const normalizedTarget = this.normalizeAngularTarget(target);

    // Check if the target route belongs to a legacy module
    const legacyModule = this.findLegacyModuleByRoute(normalizedTarget);

    if (legacyModule) {
      this.openLegacyRoute(normalizedTarget);
      return;
    }

    // Proceed with normal Angular navigation
    const navigationPromise = normalizedTarget.startsWith('/')
      ? this.router.navigateByUrl(normalizedTarget)
      : this.router.navigate([normalizedTarget], {});

    navigationPromise.then(
      (didNavigate) => {
        if (didNavigate) {
          console.log('Navigated to ' + normalizedTarget);
          this.currentTargetSignal.set(normalizedTarget);
          return;
        }

        console.log('Navigation was canceled or skipped for ' + normalizedTarget);
      },
      (error) => {
        console.log('Error navigating to ' + normalizedTarget);
        console.error(error);
      }
    );
  }

  private normalizeAngularTarget(target: string): string {
    const trimmedTarget = target.trim();
    if (!trimmedTarget) {
      return trimmedTarget;
    }

    // Convert same-origin absolute URLs to app-relative router targets.
    if (/^https?:\/\//i.test(trimmedTarget)) {
      try {
        const parsed = new URL(trimmedTarget);
        if (parsed.origin === window.location.origin) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch {
        // Keep original target when URL parsing fails.
      }
    }

    return trimmedTarget;
  }

  private findLegacyModuleByRoute(target: string): typeof this.moduleConfigService.legacyModules[number] | undefined {
    const normalizedTarget = target.startsWith('/') ? target : '/' + target;
    return this.moduleConfigService.legacyModules.find(module =>
      normalizedTarget.startsWith(module.route)
    );
  }

  private buildLegacyUrl(targetRoute: string, token: string): string {
    const customerKey = this.customerService.getCustomerKey();
    if (!customerKey) {
      throw new Error('Missing customer key for legacy redirect');
    }

    const normalizedRoute = targetRoute.startsWith('/') ? targetRoute : '/' + targetRoute;
    const baseUrl = environment.legacyFrontend.url;
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';

    const legacyUrl = new URL(`user-switch/sso/${encodeURIComponent(customerKey)}/${encodeURIComponent(token)}`, cleanBaseUrl);
    legacyUrl.searchParams.set('route', normalizedRoute);

    return legacyUrl.toString();
  }

  public openLegacyRoute(targetRoute: string, target = '_blank'): void {
    this.apiService.getLegacyToken().subscribe({
      next: ({token}) => {
        try {
          const legacyUrl = this.buildLegacyUrl(targetRoute, token);
          window.open(legacyUrl, target);
          console.log('Redirected to legacy frontend: ' + legacyUrl);
        } catch (error) {
          console.error('Error building legacy redirect URL', error);
        }
      },
      error: (error) => {
        console.error('Error getting legacy token', error);
      }
    });
  }

  public getCurrentUrl(): string {
    return this.router.url;
  }

  public setPostLoginRedirect(target: string | null | undefined): void {
    const sanitizedTarget = this.sanitizePostLoginRedirect(target);
    if (!sanitizedTarget) {
      return;
    }

    try {
      sessionStorage.setItem(this.postLoginRedirectStorageKey, sanitizedTarget);
    } catch (error) {
      console.error('Error storing post login redirect target', error);
    }
  }

  private sanitizePostLoginRedirect(target: string | null | undefined): string | null {
    if (!target || !target.startsWith('/')) {
      return null;
    }

    // Avoid redirecting back into auth routes after login.
    if (target.startsWith('/auth') && target !== '/auth/login/notes') {
      return null;
    }

    return target;
  }

  private consumePostLoginRedirect(): string | null {
    try {
      const target = sessionStorage.getItem(this.postLoginRedirectStorageKey);
      if (target) {
        sessionStorage.removeItem(this.postLoginRedirectStorageKey);
      }
      return target;
    } catch (error) {
      console.error('Error reading post login redirect target', error);
      return null;
    }
  }

  private startNavigationEffect: EffectRef = effect(() => {
    const startPage = this.customerService.customerResource()?.home?.start_page;
    if(this.triggerStartNavigation() && startPage) {
      this.triggerStartNavigation.set(false);
      this.navigateTo(this.moduleConfigService.getRouteForLandingModule(startPage));
    }
  });

  public navigateToStart(): void {
    const postLoginRedirectTarget = this.consumePostLoginRedirect();
    if (postLoginRedirectTarget) {
      this.navigateTo(postLoginRedirectTarget);
      return;
    }

    this.triggerStartNavigation.set(true);
  }

  public openURL(url: string, target = '_blank'): void {
    if(this.checkIfUrlIsCustomerForm(url)) {
      this.apiService.getLegacyToken().subscribe({
        next: ({token}) => {
          const urlWithToken = new URL(url);
          urlWithToken.searchParams.set('token', token);
          window.open(urlWithToken.toString(), target);
        },
        error: (error) => {
          console.error('Error getting legacy token', error);
          window.open(url, target);
        }
      });
    } else {
      window.open(url, target);
    }
  }

  public checkIfUrlIsCustomerForm(url: string): boolean {
    const customerFormHostname = this.sanitizeHostname(environment.customerForms.domain);
    const targetHostname = this.sanitizeHostname(url);

    return !!customerFormHostname && !!targetHostname && targetHostname === customerFormHostname;
  }

  private sanitizeHostname (value: string): string | null {
    try {
      return new URL(value).hostname.replace(/^www\./i, '');
    } catch {
      return null;
    }
  };
}
